import type { IPendingRequest } from "src/redux/slices/pendingRequestsSlice";

export type PendingRequestGroup = {
  entryId: string;
  logbookName?: string;
  save?: IPendingRequest;
  checklists: IPendingRequest[];
  sortKey: number;
};

export type PendingRequestDisplayItem =
  | { kind: "group"; group: PendingRequestGroup }
  | { kind: "row"; req: IPendingRequest; sortKey: number };

const readEntryKey = (req: IPendingRequest) => {
  const [type, entryId] = req.req.split("__");
  if (!entryId) return null;
  if (type === "save-entry" || type === "checklist-action") {
    return { type, entryId } as const;
  }
  return null;
};

export const groupPendingRequests = (
  requests: IPendingRequest[],
): PendingRequestDisplayItem[] => {
  const saves = new Map<string, IPendingRequest>();
  const checklists = new Map<string, IPendingRequest[]>();
  const groupedReqIds = new Set<string>();

  requests.forEach(req => {
    const key = readEntryKey(req);
    if (!key) return;

    groupedReqIds.add(req.req);
    if (key.type === "save-entry") {
      saves.set(key.entryId, req);
      return;
    }

    const list = checklists.get(key.entryId) ?? [];
    list.push(req);
    checklists.set(key.entryId, list);
  });

  const entryIds = new Set([...saves.keys(), ...checklists.keys()]);
  const blocks: PendingRequestDisplayItem[] = [];

  entryIds.forEach(entryId => {
    const save = saves.get(entryId);
    const entryChecklists = checklists.get(entryId) ?? [];
    if (!save && entryChecklists.length === 0) return;

    const sortKey = Math.max(
      save?.created ?? 0,
      ...entryChecklists.map(req => req.created),
    );
    const logbookName =
      save?.lbname ?? entryChecklists.find(req => req.lbname)?.lbname;

    blocks.push({
      kind: "group",
      group: {
        entryId,
        logbookName,
        save,
        checklists: entryChecklists,
        sortKey,
      },
    });
  });

  requests.forEach(req => {
    if (groupedReqIds.has(req.req)) return;
    blocks.push({ kind: "row", req, sortKey: req.created });
  });

  return blocks.sort((a, b) => {
    const aKey = a.kind === "group" ? a.group.sortKey : a.sortKey;
    const bKey = b.kind === "group" ? b.group.sortKey : b.sortKey;
    return bKey - aKey;
  });
};
